import { apiApprovalPolicies } from "@/api/approval/approval-policy.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { approvalCategoriesKeys } from "@/queries/approval/use-approval-categories.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ApiApprovalPolicyPayload } from "@/types/approvals/approval-policy.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const approvalPoliciesKeys = createQueryKeys("approvalPolicies", {
  policy: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Policy id is required");

      const result = await apiApprovalPolicies.getPolicyById(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
});

export const useApprovalPolicyDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...approvalPoliciesKeys.policy(id),
    enabled: enabled && !!id,
  });
};

export interface ApprovalPolicySaveParams {
  id: number;
  payload: ApiApprovalPolicyPayload;
}

export const useApprovalPolicySave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ id, payload }: ApprovalPolicySaveParams) {
      const result =
        id == 0
          ? await apiApprovalPolicies.createPolicy(payload)
          : await apiApprovalPolicies.updatePolicy(id, payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: (policy, { payload }) => {
      queryClient.setQueryData(approvalPoliciesKeys.policy(policy.id).queryKey, policy);
      queryClient.invalidateQueries({
        queryKey: approvalCategoriesKeys.all.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: approvalCategoriesKeys.byId(payload.category_id).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useApprovalDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      policyId,
      categoryId,
    }: {
      policyId: number;
      categoryId: number;
    }) {
      const { success, error } = await apiApprovalPolicies.deletePolicy(policyId);

      if (error || !success) throw new ApiErrorException(error);

      return { policyId, categoryId };
    },
    onSuccess: ({ policyId, categoryId }) => {
      queryClient.setQueryData(approvalPoliciesKeys.policy(policyId).queryKey, undefined);
      queryClient.invalidateQueries({
        queryKey: approvalCategoriesKeys.all.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: approvalCategoriesKeys.byId(categoryId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApprovalPoliciesSort = (categoryId: number) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function (ids: number[]) {
      const { success, error } = await apiApprovalPolicies.sort(ids);

      if (error || !success) throw new ApiErrorException(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: approvalCategoriesKeys.byId(categoryId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: approvalCategoriesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
